import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaignStats } from '../hooks/useCampaigns';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import { formatNumber, formatPercentage } from '../utils/formatters';

type MetricTone = 'blue' | 'emerald' | 'violet' | 'slate';

const toneClasses: Record<MetricTone, { icon: string; text: string; bar: string }> = {
    blue: {
        icon: 'bg-blue-100 text-blue-700',
        text: 'text-blue-700',
        bar: 'bg-blue-600',
    },
    emerald: {
        icon: 'bg-emerald-100 text-emerald-700',
        text: 'text-emerald-700',
        bar: 'bg-emerald-600',
    },
    violet: {
        icon: 'bg-violet-100 text-violet-700',
        text: 'text-violet-700',
        bar: 'bg-violet-600',
    },
    slate: {
        icon: 'bg-slate-100 text-slate-700',
        text: 'text-slate-900',
        bar: 'bg-slate-800',
    },
};

const MetricCard: React.FC<{
    label: string;
    value: string;
    detail?: string;
    tone: MetricTone;
    icon: React.ReactNode;
}> = ({ label, value, detail, tone, icon }) => (
    <div className="rounded-3xl border border-white/70 bg-white/70 p-6 shadow-xl shadow-blue-900/5 backdrop-blur-xl">
        <div className="flex items-start justify-between gap-4">
            <div>
                <p className="text-sm font-medium text-slate-600">{label}</p>
                <p className={`mt-3 text-3xl font-bold tracking-tight ${toneClasses[tone].text}`}>{value}</p>
            </div>
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${toneClasses[tone].icon}`}>
                {icon}
            </div>
        </div>
        {detail && <p className="mt-5 text-sm font-medium text-slate-500">{detail}</p>}
    </div>
);

const MiniIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {children}
    </svg>
);

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { stats, loading, error } = useCampaignStats();

    if (loading) {
        return <LoadingSpinner fullScreen text="Loading dashboard..." />;
    }

    if (error || !stats) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
                <div className="max-w-md rounded-3xl border border-red-100 bg-white p-8 text-center shadow-sm">
                    <h2 className="text-2xl font-bold text-slate-900">Dashboard unavailable</h2>
                    <p className="mt-2 text-red-600">{error || 'Failed to load statistics'}</p>
                </div>
            </div>
        );
    }

    const currency = stats.currency || 'GHS';
    const draftCampaigns = Math.max(
        0,
        stats.total_campaigns - stats.completed_campaigns - stats.active_campaigns
    );
    const messageAttempts = stats.total_messages_sent + stats.total_messages_failed;
    const sendProgress = messageAttempts > 0
        ? Math.round((stats.total_messages_sent / messageAttempts) * 100)
        : 0;
    const completedShare = stats.total_campaigns > 0
        ? Math.round((stats.completed_campaigns / stats.total_campaigns) * 100)
        : 0;
    const activeShare = stats.total_campaigns > 0
        ? Math.round((stats.active_campaigns / stats.total_campaigns) * 100)
        : 0;

    return (
        <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(219,234,254,0.95),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(240,171,252,0.35),_transparent_30%),linear-gradient(180deg,_#f8fafc_0%,_#eef4ff_100%)]">
            <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-white/60 shadow-xl shadow-blue-900/5 backdrop-blur-xl">
                    <div className="grid gap-6 p-6 lg:grid-cols-[1.3fr_0.7fr] lg:p-8">
                        <div>
                            <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">Hermes Command Center</p>
                            <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-950">Campaign Overview</h1>
                            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                                Monitor send performance, revenue, and campaign workload from one focused workspace.
                            </p>
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <Button onClick={() => navigate('/campaigns/create')}>Create Campaign</Button>
                                <Button variant="outline" onClick={() => navigate('/campaigns')}>Open Campaigns</Button>
                                <Button variant="outline" onClick={() => navigate('/admin')}>Admin Console</Button>
                            </div>
                        </div>

                        <div className="rounded-3xl border border-white/80 bg-white/70 p-5 shadow-sm">
                            <p className="text-sm font-semibold text-slate-900">Today’s Workload</p>
                            <div className="mt-5 space-y-4">
                                <div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-600">Completed campaigns</span>
                                        <span className="font-semibold text-slate-950">{completedShare}%</span>
                                    </div>
                                    <div className="mt-2 h-2 rounded-full bg-slate-100">
                                        <div className="h-2 rounded-full bg-emerald-600" style={{ width: `${completedShare}%` }} />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-600">Active campaigns</span>
                                        <span className="font-semibold text-slate-950">{activeShare}%</span>
                                    </div>
                                    <div className="mt-2 h-2 rounded-full bg-slate-100">
                                        <div className="h-2 rounded-full bg-blue-600" style={{ width: `${activeShare}%` }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <MetricCard
                        label="Total Campaigns"
                        value={formatNumber(stats.total_campaigns)}
                        detail={`${formatNumber(stats.active_campaigns)} active`}
                        tone="blue"
                        icon={<MiniIcon><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 3h7l3 3v15H7z" /></MiniIcon>}
                    />
                    <MetricCard
                        label="Messages Sent"
                        value={formatNumber(stats.total_messages_sent)}
                        detail={`${formatNumber(stats.total_messages_failed)} failed attempts`}
                        tone="emerald"
                        icon={<MiniIcon><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a10 10 0 01-4.255-.949L3 20l1.395-3.72A7.6 7.6 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></MiniIcon>}
                    />
                    <MetricCard
                        label="Send Success"
                        value={formatPercentage(stats.overall_success_rate)}
                        detail="Based on accepted sends"
                        tone="violet"
                        icon={<MiniIcon><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 19v-6m5 6V9m5 10v-8m5 8V5" /></MiniIcon>}
                    />
                    <MetricCard
                        label="Actual Revenue"
                        value={`${currency} ${formatNumber(stats.actual_revenue || 0)}`}
                        detail={`Est. ${currency} ${formatNumber(stats.estimated_revenue || 0)}`}
                        tone="slate"
                        icon={<MiniIcon><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-2.21 0-4 .895-4 2s1.79 2 4 2 4 .895 4 2-1.79 2-4 2m0-8V6m0 12v-2" /></MiniIcon>}
                    />
                </section>

                <section className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                    <div className="rounded-3xl border border-white/70 bg-white/70 p-6 shadow-xl shadow-blue-900/5 backdrop-blur-xl">
                        <h2 className="text-lg font-semibold text-slate-950">Campaign Status</h2>
                        <div className="mt-5 space-y-4">
                            {[
                                { label: 'Completed', value: stats.completed_campaigns, color: 'bg-emerald-600' },
                                { label: 'Active', value: stats.active_campaigns, color: 'bg-blue-600' },
                                { label: 'Draft', value: draftCampaigns, color: 'bg-slate-500' },
                            ].map((item) => {
                                const width = stats.total_campaigns > 0
                                    ? Math.round((item.value / stats.total_campaigns) * 100)
                                    : 0;
                                return (
                                    <div key={item.label}>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600">{item.label}</span>
                                            <span className="font-semibold text-slate-950">{formatNumber(item.value)}</span>
                                        </div>
                                        <div className="mt-2 h-2 rounded-full bg-slate-100">
                                            <div className={`h-2 rounded-full ${item.color}`} style={{ width: `${width}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="rounded-3xl border border-white/70 bg-white/70 p-6 shadow-xl shadow-blue-900/5 backdrop-blur-xl">
                        <h2 className="text-lg font-semibold text-slate-950">Message Operations</h2>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div className="rounded-2xl bg-slate-50/80 p-4">
                                <p className="text-sm text-slate-500">Accepted</p>
                                <p className="mt-2 text-2xl font-bold text-slate-950">{formatNumber(stats.total_messages_sent)}</p>
                            </div>
                            <div className="rounded-2xl bg-red-50/80 p-4">
                                <p className="text-sm text-red-600">Failed</p>
                                <p className="mt-2 text-2xl font-bold text-red-700">{formatNumber(stats.total_messages_failed)}</p>
                            </div>
                            <div className="rounded-2xl bg-violet-50/80 p-4">
                                <p className="text-sm text-violet-600">Success</p>
                                <p className="mt-2 text-2xl font-bold text-violet-700">{sendProgress}%</p>
                            </div>
                        </div>
                        <div className="mt-5 rounded-2xl border border-slate-200 bg-white/60 p-4">
                            <div className="flex justify-between text-sm">
                                <span className="font-medium text-slate-600">Delivery receipts</span>
                                <span className="font-semibold text-slate-950">{formatNumber(stats.total_messages_delivered)}</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-500">
                                Receipts are tracked separately; success currently reflects accepted sends.
                            </p>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Dashboard;
